import { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LoginPage = ({ onLogin }) => {
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showEmployeeLogin, setShowEmployeeLogin] = useState(false);
  const [appSettings, setAppSettings] = useState({
    app_title: 'GB-Sylt-Wissen',
    app_subtitle: 'Wissens- & Chat-System',
    btn_guest_tab: 'Gast-Zugang',
    btn_employee_tab: 'Mitarbeiter',
    btn_guest_continue: 'Als Gast fortfahren'
  });

  useEffect(() => {
    loadAppSettings();
  }, []);

  const loadAppSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setAppSettings({
        app_title: response.data.app_title || 'GB-Sylt-Wissen',
        app_subtitle: response.data.app_subtitle || 'Wissens- & Chat-System',
        btn_guest_tab: response.data.btn_guest_tab || 'Gast-Zugang',
        btn_employee_tab: response.data.btn_employee_tab || 'Mitarbeiter',
        btn_guest_continue: response.data.btn_guest_continue || 'Als Gast fortfahren'
      });
    } catch (error) {
      console.error('Fehler beim Laden der App-Einstellungen:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: loginData.username,
        password: loginData.password
      });

      if (response.data.token) {
        onLogin({
          name: response.data.user.name,
          role: response.data.user.role,
          username: response.data.user.username,
          token: response.data.token,
          must_change_password: response.data.user.must_change_password || false
        });
      } else {
        setError('Ungültige Anmeldedaten. Bitte versuchen Sie es erneut.');
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMsg = error.response?.data?.detail || 'Ungültige Anmeldedaten. Bitte versuchen Sie es erneut.';
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGuestAccess = () => {
    onLogin({ name: 'Gast', role: 'guest' });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex h-16 w-16 rounded-2xl bg-blue-600 items-center justify-center mb-4">
            <span className="text-white font-bold text-2xl">GB</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{appSettings.app_title}</h1>
          <p className="text-gray-500 dark:text-gray-400">{appSettings.app_subtitle}</p>
        </div>

        <div className="space-y-4">
          {!showEmployeeLogin ? (
            <>
              {/* Gast-Zugang */}
              <div 
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 cursor-pointer hover:border-blue-500 hover:shadow-md transition-all"
                onClick={handleGuestAccess}
              >
                <div className="text-center space-y-4">
                  <div className="mx-auto w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                    <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{appSettings.btn_guest_tab}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Fragen zu Ihrer Unterkunft ohne Anmeldung
                    </p>
                  </div>
                </div>
              </div>

              {/* Mitarbeiter-Zugang */}
              <div 
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 cursor-pointer hover:border-blue-500 hover:shadow-md transition-all"
                onClick={() => setShowEmployeeLogin(true)}
              >
                <div className="text-center space-y-4">
                  <div className="mx-auto w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                    <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{appSettings.btn_employee_tab}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Anmeldung für Mitarbeiter und Team
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* Login Form */
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{appSettings.btn_employee_tab}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Melden Sie sich mit Ihren Zugangsdaten an
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowEmployeeLogin(false);
                      setError('');
                      setLoginData({ username: '', password: '' });
                    }}
                    className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <form onSubmit={handleLogin} className="space-y-4">
                  {error && (
                    <div className="p-3 rounded-lg bg-red-100 border border-red-200 text-red-700 text-sm flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Benutzername
                    </label>
                    <input
                      id="username"
                      type="text"
                      placeholder="Benutzername"
                      value={loginData.username}
                      onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                      required
                      autoComplete="username"
                      autoFocus
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Passwort
                    </label>
                    <input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      autoComplete="current-password"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Anmelden...' : 'Anmelden'}
                  </button>
                </form>

              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          © 2024 GB Sylt GmbH. Alle Rechte vorbehalten.
        </p>
      </div>
    </div>
  );
};
